import { UNSAFE_isExperimentEnabledNoExposure } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { AdfStats } from '../useAdfStats.tsx';

type IsComplexPageReason = 'TooManyNodes' | 'TooManyExtensions' | undefined;

type UseProgressiveRenderingConfigResponseType = {
	isComplexPageReason: IsComplexPageReason;
	batchSize: number;
};

type UseProgressiveRenderingConfigProps = {
	skip?: boolean;
	totalNodeCount: AdfStats['totalNodeCount'];
	totalNodesHiddenFromView: AdfStats['totalNodesHiddenFromView'];
	nodeCountByType: AdfStats['nodeCountByType'];
};

export const useProgressiveRenderingConfig = ({
	skip,
	totalNodeCount,
	totalNodesHiddenFromView,
	nodeCountByType,
}: UseProgressiveRenderingConfigProps): UseProgressiveRenderingConfigResponseType => {
	let isComplexPageReason: IsComplexPageReason;
	let batchSize = 0;

	if (!skip) {
		const defaultConfig = StatsigConfigurations.getString(
			'cc_complexit_fe_progressive_adf_rendering_configs',
			'',
		).split(',');
		const batchSizeConfig = Number(defaultConfig[0]) || 1000;
		const multiplierConfig = Number(defaultConfig[1]) || 1;

		// A collapsed expand shows the text of its body rather than rendering it, so those nodes cost
		// the page nothing and the reader cannot see them either way. Counting them makes a page the
		// reader experiences as short look complex, and batching a short page only delays it.
		//
		// Read without firing exposure: this runs for every page, while the renderer fires exposure
		// where the behaviour actually differs — a page that has an expand on it.
		const nodeCount = UNSAFE_isExperimentEnabledNoExposure(
			'platform_editor_defer_collapsed_expand_body',
		)
			? totalNodeCount - totalNodesHiddenFromView
			: totalNodeCount;

		// Figure out whether progressive rendering is to be enabled:
		if (nodeCount >= batchSizeConfig * multiplierConfig) {
			isComplexPageReason = 'TooManyNodes';
			batchSize = batchSizeConfig;
		} else if (
			(nodeCountByType['extension'] || 0) +
				(nodeCountByType['inlineExtension'] || 0) +
				(nodeCountByType['bodiedExtension'] || 0) +
				(nodeCountByType['multiBodiedExtension'] || 0) >
			4
		) {
			isComplexPageReason = 'TooManyExtensions';
		}
	}

	return {
		isComplexPageReason,
		batchSize,
	};
};
